import React from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { MdAdd, MdClose } from "react-icons/md";
import { Switch } from "@mui/material";
import Input from "../ui/Input";

const RentalIncomeForm = ({ control, watch, setValue, isPOI = false, regime = "old" }) => {
  const {
    fields: rentalIncomeFields,
    append: appendRentalIncome,
    remove: removeRentalIncome,
  } = useFieldArray({
    control,
    name: "rental_income",
  });

  const isReceivingRentalIncome = watch("rental_income_applicable") === 1;

  const financialYearId = watch("financial_year_id");

  const addRentalIncome = () => {
    const newRentalIncome = {
      financial_year_id: financialYearId,
      annual_rent_received: "0",
      municipal_taxes_paid: "0",
      net_annual_value: "0",
      standard_deduction: "0",
      repaying_home_loan: false,
      repaying_home_loan_data: {
        principal_amount: 0,
        interest_amount: 0,
        name_of_lender: "",
        lender_pan: "",
      },
      net_income_loss: "0",
    };

    // Add actual amount fields for POI mode
    if (isPOI) {
      newRentalIncome.actual_annual_rent_received = "0";
      newRentalIncome.actual_municipal_taxes_paid = "0";
      newRentalIncome.repaying_home_loan_data.actual_principal_amount = 0;
      newRentalIncome.repaying_home_loan_data.actual_interest_amount = 0;
    }

    appendRentalIncome(newRentalIncome);
  };

  const calculateValues = (index) => {
    const watchedFields = watch("rental_income");
    
    if (watchedFields && watchedFields[index]) {
      // Get declared values
      const declaredAnnualRent = parseFloat(watchedFields[index].annual_rent_received) || 0;
      const declaredMunicipalTaxes = parseFloat(watchedFields[index].municipal_taxes_paid) || 0;
      const declaredInterest = parseFloat(watchedFields[index]?.repaying_home_loan_data?.interest_amount) || 0;

      // Calculate declared values
      const declaredNetAnnualValue = declaredAnnualRent - declaredMunicipalTaxes;
      const declaredStandardDeduction = declaredNetAnnualValue * 0.3;
      
      // Calculate declared net income/loss based on regime
      let declaredNetIncomeLoss;
      if (regime === "old") {
        // Old regime: Include interest deduction for rental property
        declaredNetIncomeLoss = declaredNetAnnualValue - declaredStandardDeduction - declaredInterest;
      } else {
        // New regime: No interest deduction for rental property
        declaredNetIncomeLoss = declaredNetAnnualValue - declaredStandardDeduction;
      }

      // Update declared calculated fields
      setValue(`rental_income.${index}.net_annual_value`, declaredNetAnnualValue.toFixed(2));
      setValue(`rental_income.${index}.standard_deduction`, declaredStandardDeduction.toFixed(2));
      setValue(`rental_income.${index}.net_income_loss`, declaredNetIncomeLoss.toFixed(2));

      // If in POI mode, also calculate actual values
      if (isPOI) {
        const actualAnnualRent = parseFloat(watchedFields[index].actual_annual_rent_received) || 0;
        const actualMunicipalTaxes = parseFloat(watchedFields[index].actual_municipal_taxes_paid) || 0;
        const actualInterest = parseFloat(watchedFields[index]?.repaying_home_loan_data?.actual_interest_amount) || 0;

        // Calculate actual values
        const actualNetAnnualValue = actualAnnualRent - actualMunicipalTaxes;
        const actualStandardDeduction = actualNetAnnualValue * 0.3;
        
        // Calculate actual net income/loss based on regime
        let actualNetIncomeLoss;
        if (regime === "old") {
          // Old regime: Include interest deduction for rental property
          actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction - actualInterest;
        } else {
          // New regime: No interest deduction for rental property
          actualNetIncomeLoss = actualNetAnnualValue - actualStandardDeduction;
        }

        // Update actual calculated fields
        setValue(`rental_income.${index}.actual_net_annual_value`, actualNetIncomeLoss.toFixed(2));
        setValue(`rental_income.${index}.actual_standard_deduction`, actualStandardDeduction.toFixed(2));
        setValue(`rental_income.${index}.actual_net_income_loss`, actualNetIncomeLoss.toFixed(2));
      }
    }
  };

  // trigger recalculation when interest field changes
  const handleInterestChange = (index) => (e) => {
    setTimeout(() => calculateValues(index), 0);
  };

  const handleAnnualRentChange = (index) => (e) => {
    // Let the form handle the change first, then calculate
    setTimeout(() => calculateValues(index), 0);
  };

  const handleMunicipalTaxesChange = (index) => (e) => {
    // Let the form handle the change first, then calculate
    setTimeout(() => calculateValues(index), 0);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toggle Header */}
      <div className="flex items-center justify-between p-4 bg-white">
        <span className="text-base font-medium text-gray-900">
          Is the employee receiving rental income from let out property?
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {isReceivingRentalIncome ? "Yes" : "No"}
          </span>
          <Controller
            control={control}
            name="rental_income_applicable"
            render={({ field }) => (
              <Switch
                checked={field.value === 1 ? true : false}
                onChange={(e) => {
                  field.onChange(e.target.checked ? 1 : 0); // Update form value
                }}
                color="primary"
                size="medium"
              />
            )}
          />
        </div>
      </div>

      {/* Rental Income Details Form */}
      {isReceivingRentalIncome && (
        <div className="border-t border-gray-200 px-6 py-6 space-y-8 bg-white">
          {rentalIncomeFields.map((field, index) => (
            <div key={field.id} className="space-y-6 relative">
              {/* Rental Income Details header with close button */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Let Out Property Details
                </h2>
                {rentalIncomeFields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRentalIncome(index)}
                    className="text-gray-400 hover:text-red-500"
                    aria-label="Remove let out property"
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* Form fields layout based on isPOI */}
              {isPOI ? (
                // POI Mode: 3-column layout aligned with sticky header
                <div className="space-y-6">
                  {/* Annual Rent Received Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-1/3 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Annual Rent Received
                      </label>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.annual_rent_received`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          disabled={true}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.actual_annual_rent_received`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          onChangeFn={handleAnnualRentChange(index)}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Municipal Taxes Paid Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-1/3 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Municipal Taxes Paid
                      </label>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.municipal_taxes_paid`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          disabled={true}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.actual_municipal_taxes_paid`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          onChangeFn={handleMunicipalTaxesChange(index)}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Net Annual Value Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-1/3 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Net Annual Value
                      </label>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.net_annual_value`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.actual_net_annual_value`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Standard Deduction Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-1/3 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Standard Deduction (@ 30% of Net Annual Value)
                      </label>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.standard_deduction`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                    <div className="w-1/3 flex justify-center">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.actual_standard_deduction`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checkbox for repaying home loan */}
                  <div className="flex w-full gap-4">
                    <div className="w-1/3 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Repaying Home Loan for This Property
                      </label>
                    </div>
                    <div className="w-2/3 flex items-center">
                      <Controller
                        control={control}
                        name={`rental_income.${index}.repaying_home_loan`}
                        render={({ field: { onChange, value } }) => (
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => {
                              if (e.target.checked) {
                                const current = watch("rental_income") || [];
                                const existing = current[index]?.repaying_home_loan_data;
                                if (!existing) {
                                  setValue(`rental_income.${index}.repaying_home_loan_data`, {
                                    principal_amount: 0,
                                    actual_principal_amount: 0,
                                    interest_amount: 0,
                                    actual_interest_amount: 0,
                                    name_of_lender: "",
                                    lender_pan: "",
                                  });
                                }
                              }
                              onChange(e.target.checked);
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Home loan details - POI mode */}
                  <Controller
                    control={control}
                    name={`rental_income.${index}.repaying_home_loan`}
                    render={({ field: { value: isRepaying } }) =>
                      isRepaying ? (
                        <div className="pl-4 border-l-2 border-gray-100 space-y-6">
                          {/* Principal Amount Row - only show for old regime */}
                          {regime === "old" && (
                            <div className="flex w-full gap-4">
                              <div className="w-3/5 flex items-center">
                                <label className="text-sm font-medium text-gray-900">
                                  Principal Paid on Home Loan for Let Out Property
                                </label>
                              </div>
                              <div className="w-2/5 flex justify-end">
                                <div className="w-full max-w-xs">
                                  <Input
                                    control={control}
                                    name={`rental_income.${index}.repaying_home_loan_data.principal_amount`}
                                    type="number"
                                    isCurrency={true}
                                    placeholder="0"
                                    isPositiveNumber={true}
                                    disabled={true}
                                    hideLabel={true}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Interest Amount Row */}
                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Interest Paid on Home Loan
                                {regime === "new" && (
                                  <span className="text-xs text-orange-600 block">(Not deductible in New Regime)</span>
                                )}
                              </label>
                            </div>
                            <div className="w-2/5 flex justify-end">
                              <div className="w-full max-w-xs">
                                <Input
                                  control={control}
                                  name={`rental_income.${index}.repaying_home_loan_data.interest_amount`}
                                  type="number"
                                  isCurrency={true}
                                  placeholder="0"
                                  isPositiveNumber={true}
                                  disabled={true}
                                  hideLabel={true}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Lender Details */}
                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Name of the Lender
                              </label>
                            </div>
                            <div className="w-2/5">
                              <Input
                                control={control}
                                name={`rental_income.${index}.repaying_home_loan_data.name_of_lender`}
                                placeholder="Enter lender name"
                                hideLabel={true}
                              />
                            </div>
                          </div>

                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Lender PAN
                              </label>
                            </div>
                            <div className="w-2/5">
                              <Input
                                control={control}
                                name={`rental_income.${index}.repaying_home_loan_data.lender_pan`}
                                placeholder="ABCDE1234F"
                                hideLabel={true}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null
                    }
                  />

                  {/* Net Income/Loss Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Net Income/Loss from House Property
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.net_income_loss`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Declaration Mode: 2-column layout with separate labels
                <div className="space-y-6">
                  {/* Annual Rent Received Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Annual Rent Received
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.annual_rent_received`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          onChangeFn={handleAnnualRentChange(index)}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Municipal Taxes Paid Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Municipal Taxes Paid
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.municipal_taxes_paid`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          isPositiveNumber={true}
                          onChangeFn={handleMunicipalTaxesChange(index)}
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Net Annual Value Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Net Annual Value
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.net_annual_value`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Standard Deduction Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Standard Deduction (@ 30% of Net Annual Value)
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.standard_deduction`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checkbox for repaying home loan */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Repaying Home Loan for This Property
                      </label>
                    </div>
                    <div className="w-2/5 flex items-center">
                      <Controller
                        control={control}
                        name={`rental_income.${index}.repaying_home_loan`}
                        render={({ field: { onChange, value } }) => (
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => {
                              // ensure nested data exists when toggling on
                              if (e.target.checked) {
                                // if repaying_home_loan_data not present, initialize it
                                const current = watch("rental_income") || [];
                                const existing = current[index]?.repaying_home_loan_data;
                                if (!existing) {
                                  setValue(`rental_income.${index}.repaying_home_loan_data`, {
                                    principal_amount: 0,
                                    interest_amount: 0,
                                    name_of_lender: "",
                                    lender_pan: "",
                                  });
                                }
                              }
                              onChange(e.target.checked);
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Repaying home loan details block */}
                  <Controller
                    control={control}
                    name={`rental_income.${index}.repaying_home_loan`}
                    render={({ field: { value: isRepaying } }) =>
                      isRepaying ? (
                        <div className="pl-4 border-l-2 border-gray-100 space-y-6">
                          {/* Principal Amount Row - only show for old regime */}
                          {regime === "old" && (
                            <div className="flex w-full gap-4">
                              <div className="w-3/5 flex items-center">
                                <label className="text-sm font-medium text-gray-900">
                                  Principal Paid on Home Loan for Let Out Property
                                </label>
                              </div>
                              <div className="w-2/5 flex justify-end">
                                <div className="w-full max-w-xs">
                                  <Input
                                    control={control}
                                    name={`rental_income.${index}.repaying_home_loan_data.principal_amount`}
                                    type="number"
                                    isCurrency={true}
                                    placeholder="0"
                                    isPositiveNumber={true}
                                    hideLabel={true}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Interest Amount Row */}
                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Interest Paid on Home Loan
                                {regime === "new" && (
                                  <span className="text-xs text-orange-600 block">(Not deductible in New Regime)</span>
                                )}
                              </label>
                            </div>
                            <div className="w-2/5 flex justify-end">
                              <div className="w-full max-w-xs">
                                <Input
                                  control={control}
                                  name={`rental_income.${index}.repaying_home_loan_data.interest_amount`}
                                  type="number"
                                  isCurrency={true}
                                  placeholder="0"
                                  isPositiveNumber={true}
                                  onChangeFn={handleInterestChange(index)}
                                  hideLabel={true}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Lender Details */}
                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Name of the Lender
                              </label>
                            </div>
                            <div className="w-2/5">
                              <Input
                                control={control}
                                name={`rental_income.${index}.repaying_home_loan_data.name_of_lender`}
                                placeholder="Enter lender name"
                                hideLabel={true}
                              />
                            </div>
                          </div>

                          <div className="flex w-full gap-4">
                            <div className="w-3/5 flex items-center">
                              <label className="text-sm font-medium text-gray-900">
                                Lender PAN
                              </label>
                            </div>
                            <div className="w-2/5">
                              <Input
                                control={control}
                                name={`rental_income.${index}.repaying_home_loan_data.lender_pan`}
                                placeholder="ABCDE1234F"
                                hideLabel={true}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null
                    }
                  />

                  {/* Net Income/Loss Row */}
                  <div className="flex w-full gap-4">
                    <div className="w-3/5 flex items-center">
                      <label className="text-sm font-medium text-gray-900">
                        Net Income/Loss from House Property
                      </label>
                    </div>
                    <div className="w-2/5 flex justify-end">
                      <div className="w-full max-w-xs">
                        <Input
                          control={control}
                          name={`rental_income.${index}.net_income_loss`}
                          type="number"
                          isCurrency={true}
                          placeholder="0"
                          disabled={true}
                          className="bg-gray-50"
                          hideLabel={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addRentalIncome}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            <MdAdd className="w-4 h-4" />
            Add a Let Out Property
          </button>
        </div>
      )}
    </div>
  );
};

export default RentalIncomeForm;

